import { Link } from 'react-router-dom';
import { RotateCcw, FileText, AlertTriangle, CreditCard, PackageCheck, Ban, Phone, Mail } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

export default function ReturnsPage() {
    return (
        <div className="min-h-screen bg-background">
            <div className="container-main py-10">
                <Breadcrumbs />

                <h1 className="font-serif text-4xl sm:text-5xl font-bold mb-8 dark:text-white" data-testid="returns-page-title">
                    Политика за възстановяване, връщане и отказ
                </h1>

                <div className="max-w-4xl space-y-8">
                    {/* Merchant info */}
                    <div className="p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl" data-testid="returns-merchant-info">
                        <h2 className="font-serif text-xl font-bold text-amber-900 dark:text-amber-300 mb-3 flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Данни за търговеца
                        </h2>
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-amber-800 dark:text-amber-200">
                            <div>
                                <p><strong>Фирма:</strong> ВКУСЪТ НА ТУРЦИЯ ЕООД (VKUSAT NA TURKEY EOOD)</p>
                                <p><strong>ЕИК:</strong> 208822640</p>
                                <p><strong>ДДС №:</strong> BG208822640</p>
                                <p><strong>МОЛ:</strong> Димитър Александров Веселинов</p>
                            </div>
                            <div>
                                <p><strong>Адрес на управление:</strong> гр. София, ул. Осогово 60</p>
                                <p><strong>Телефон:</strong> 0899 97 27 27</p>
                                <p><strong>Имейл:</strong> vkustnaturciq39@gmail.com</p>
                            </div>
                        </div>
                    </div>

                    {/* 1. Right of withdrawal */}
                    <section className="p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                        <h2 className="font-serif text-2xl font-bold flex items-center gap-2 text-green-800 dark:text-green-300 mb-3">
                            <RotateCcw className="w-6 h-6" />
                            1. Право на отказ — 14 дни
                        </h2>
                        <p className="text-green-800 dark:text-green-200">
                            Съгласно чл. 50 от Закона за защита на потребителите (ЗЗП) имате право да се откажете от
                            договора от разстояние в срок от <strong>14 календарни дни</strong>, без да посочвате причина
                            и без да дължите обезщетение или неустойка. Срокът започва да тече от датата, на която Вие
                            или посочено от Вас трето лице сте получили стоката.
                        </p>
                    </section>

                    {/* 2. Exceptions */}
                    <section className="p-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                        <h2 className="font-serif text-2xl font-bold flex items-center gap-2 text-red-800 dark:text-red-300 mb-3">
                            <Ban className="w-6 h-6" />
                            2. Изключения от правото на отказ
                        </h2>
                        <p className="text-red-800 dark:text-red-200 mb-2">
                            Съгласно чл. 57 от ЗЗП правото на отказ не се прилага за:
                        </p>
                        <ul className="list-disc pl-6 text-red-800 dark:text-red-200 space-y-1">
                            <li>Хранителни стоки, които поради своето естество могат да влошат качеството си или имат кратък срок на годност;</li>
                            <li>Запечатани стоки, които са разпечатани след доставката им и не могат да бъдат върнати поради съображения, свързани с хигиената или защита на здравето (напр. отворени хранителни продукти);</li>
                            <li>Стоки, изработени по индивидуална поръчка на потребителя.</li>
                        </ul>
                    </section>

                    {/* 3. Return procedure */}
                    <section className="p-6 bg-white dark:bg-slate-800 border border-border dark:border-slate-700 rounded-xl">
                        <h2 className="font-serif text-2xl font-bold flex items-center gap-2 dark:text-white mb-3">
                            <PackageCheck className="w-6 h-6 text-amber-600" />
                            3. Процедура за връщане
                        </h2>
                        <ol className="list-decimal pl-6 space-y-2 text-muted-foreground dark:text-slate-300">
                            <li>Уведомете ни писмено на имейл <strong>vkustnaturciq39@gmail.com</strong> или на телефон <strong>0899 97 27 27</strong> в рамките на 14-дневния срок, като посочите име, номер на поръчка и продуктите, които желаете да върнете.</li>
                            <li>Върнете стоката на адрес: <strong>гр. София, ул. Пиротска 85</strong> (магазин) в срок до 14 дни от уведомлението.</li>
                            <li>Стоката трябва да е в оригинална опаковка, неизползвана, с ненарушена цялост и всички етикети.</li>
                            <li>Преките разходи за връщане на стоката са за сметка на потребителя, освен ако стоката е дефектна или е доставена грешна стока — тогава разходите са за наша сметка.</li>
                        </ol>
                    </section>

                    {/* 4. Refunds */}
                    <section className="p-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl" data-testid="returns-refund-section">
                        <h2 className="font-serif text-2xl font-bold flex items-center gap-2 text-blue-800 dark:text-blue-300 mb-3">
                            <CreditCard className="w-6 h-6" />
                            4. Възстановяване на суми
                        </h2>
                        <ul className="list-disc pl-6 space-y-2 text-blue-800 dark:text-blue-200">
                            <li>Възстановяваме всички получени от Вас плащания, включително стандартните разходи за доставка, <strong>не по-късно от 14 дни</strong> от датата, на която сме уведомени за отказа и сме получили стоката обратно (или доказателство за изпращането ѝ).</li>
                            <li><strong>Плащане с банкова карта:</strong> сумата се възстановява по същата карта, с която е извършено плащането (чрез платежния оператор myPOS).</li>
                            <li><strong>Наложен платеж:</strong> сумата се възстановява по посочена от Вас банкова сметка.</li>
                            <li><strong>Банков превод:</strong> сумата се възстановява по сметката, от която е получено плащането.</li>
                            <li>Не дължите такси или комисиони за възстановяването.</li>
                        </ul>
                    </section>

                    {/* 5. Claims */}
                    <section className="p-6 bg-white dark:bg-slate-800 border border-border dark:border-slate-700 rounded-xl">
                        <h2 className="font-serif text-2xl font-bold flex items-center gap-2 dark:text-white mb-3">
                            <AlertTriangle className="w-6 h-6 text-amber-600" />
                            5. Рекламации
                        </h2>
                        <p className="text-muted-foreground dark:text-slate-300 mb-2">
                            При получаване на дефектна, повредена или грешна стока имате право на рекламация. Свържете се
                            с нас до 48 часа от получаването, като приложите снимки на продукта и опаковката. При основателна
                            рекламация ще заменим стоката или ще възстановим платената сума изцяло, включително разходите за доставка.
                        </p>
                        <p className="text-muted-foreground dark:text-slate-300">
                            Спорове могат да бъдат отнасяни и към Комисията за защита на потребителите (КЗП) — www.kzp.bg,
                            както и към европейската платформа за онлайн решаване на спорове — ec.europa.eu/odr.
                        </p>
                    </section>

                    {/* Contact */}
                    <section className="p-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
                        <h2 className="font-serif text-xl font-bold text-amber-900 dark:text-amber-300 mb-3">Контакт при въпроси</h2>
                        <div className="flex flex-col sm:flex-row gap-4 text-amber-800 dark:text-amber-200">
                            <a href="tel:0899972727" className="flex items-center gap-2 hover:text-amber-600"><Phone className="w-4 h-4" /> 0899 97 27 27</a>
                            <a href="mailto:vkustnaturciq39@gmail.com" className="flex items-center gap-2 hover:text-amber-600"><Mail className="w-4 h-4" /> vkustnaturciq39@gmail.com</a>
                        </div>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-3">
                            Вижте също: <Link to="/terms" className="underline">Общи условия</Link> • <Link to="/privacy" className="underline">Политика за поверителност</Link>
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
}
